import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { SmoothScroll } from "@/components/SmoothScroll";
import { Navbar } from "@/components/Navbar";
import { Hero } from "@/components/Hero";
import { PhotoTicker } from "@/components/PhotoTicker";
import { TrustedStats } from "@/components/TrustedStats";
import { PracticeAreasSection } from "@/components/PracticeAreasSection";
import { CredentialsStrip } from "@/components/CredentialsStrip";
import { ClientPortfolio } from "@/components/ClientPortfolio";
import { AnnouncementBar } from "@/components/AnnouncementBar";
import { FeaturedBriefing } from "@/components/FeaturedBriefing";
import { Footer } from "@/components/Footer";
import { ChatWidget } from "@/components/ChatWidget";
import { SiteStructuredData } from "@/components/SiteStructuredData";
import PracticeAreaPage from "@/pages/PracticeAreaPage";
import AboutPage from "@/pages/AboutPage";
import InsightsPage from "@/pages/InsightsPage";
import ArticleDetailPage from "@/pages/ArticleDetailPage";
import ContactPage from "@/pages/ContactPage";
import LegalPage from "@/pages/LegalPage";
import SitemapPage from "@/pages/SitemapPage";
import JurisdictionAtlasPage from "@/pages/JurisdictionAtlasPage";
import JurisdictionDetailPage from "@/pages/JurisdictionDetailPage";
import LoginPage from "@/pages/admin/LoginPage";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import ArticleEditor from "@/pages/admin/ArticleEditor";
import AdminEnquiries from "@/pages/admin/AdminEnquiries";
import { AuthProvider } from "@/context/AuthContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

const Layout = ({ children }) => (
  <SmoothScroll>
    <div className="min-h-screen bg-canvas antialiased">
      <SiteStructuredData />
      <Navbar />
      <main>{children}</main>
      <Footer />
      <ChatWidget />
    </div>
  </SmoothScroll>
);

const Home = () => {
  useDocumentMeta({ title: null, description: null, path: "/" });
  return (
    <Layout>
      <AnnouncementBar />
      <Hero />
      <PhotoTicker />
      <CredentialsStrip />
      <TrustedStats />
      <FeaturedBriefing />
      <PracticeAreasSection />
      <ClientPortfolio />
    </Layout>
  );
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <ScrollToTop />
        <Toaster position="top-center" richColors />
        <Routes>
          {/* Public site */}
          <Route path="/" element={<Home />} />
          <Route path="/practice/:slug" element={<Layout><PracticeAreaPage /></Layout>} />
          <Route path="/services/company-incorporation/jurisdiction-atlas" element={<Layout><JurisdictionAtlasPage /></Layout>} />
          <Route path="/services/company-incorporation/jurisdiction-atlas/:slug" element={<Layout><JurisdictionDetailPage /></Layout>} />
          <Route path="/about" element={<Layout><AboutPage /></Layout>} />
          <Route path="/insights" element={<Layout><InsightsPage /></Layout>} />
          <Route path="/insights/:slug" element={<Layout><ArticleDetailPage /></Layout>} />
          <Route path="/contact" element={<Layout><ContactPage /></Layout>} />
          <Route path="/legal/:slug" element={<Layout><LegalPage /></Layout>} />
          <Route path="/sitemap" element={<Layout><SitemapPage /></Layout>} />

          {/* Admin CMS */}
          <Route path="/admin/login" element={<LoginPage />} />
          <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
          <Route path="/admin/new" element={<ProtectedRoute><ArticleEditor /></ProtectedRoute>} />
          <Route path="/admin/edit/:id" element={<ProtectedRoute><ArticleEditor /></ProtectedRoute>} />
          <Route path="/admin/enquiries" element={<ProtectedRoute><AdminEnquiries /></ProtectedRoute>} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
